import type {
  APIResponse,
  CvDocumentSelection,
  EmployerApplicantCandidateSummary,
  EmployerApplicantCvSnapshot,
  EmployerApplicantDetail,
} from "@tabbio/types";
import { api } from "./api";
import { CvCollaborationApiError } from "./cv-collaboration-api";
export type SubmissionDisclosure = {
  hash: string;
  candidate: EmployerApplicantCandidateSummary;
  document: Omit<EmployerApplicantCvSnapshot, "createdAt" | "updatedAt"> | null;
  liveProfileIncluded: false;
};
export type CompanyProfilePreview = {
  company: { id: string; name: string };
  hash: string;
  revision: number;
  projection: {
    candidate: EmployerApplicantCandidateSummary;
    candidateProfile: EmployerApplicantDetail["candidateProfile"];
  };
};
export type CompanyProfileAccess = {
  enabled: boolean;
  companies: {
    id: string;
    name: string;
    active: boolean;
    canRevoke: boolean;
  }[];
};
function value<T>(response: APIResponse<T>): T {
  if (response.error)
    throw new CvCollaborationApiError(
      response.error.message,
      response.error.code,
    );
  if (response.data == null) throw new Error("Disclosure unavailable");
  return response.data;
}
const companyRoot = "/api/profile/company-access";
export async function fetchSubmissionDisclosure(
  jobId: string,
  cvId: string,
  cvSelection?: CvDocumentSelection,
) {
  return value(
    await api.post<SubmissionDisclosure>(
      `/api/jobs/${encodeURIComponent(jobId)}/disclosure-preview`,
      { cvId, ...(cvSelection ? { cvSelection } : {}) },
    ),
  );
}
export async function fetchCompanyProfileAccess() {
  return value(await api.get<CompanyProfileAccess>(companyRoot));
}
export async function fetchCompanyProfilePreview(companyId: string) {
  return value(
    await api.get<CompanyProfilePreview>(
      `${companyRoot}/${encodeURIComponent(companyId)}/preview`,
    ),
  );
}
export async function grantCompanyProfileAccess(
  preview: CompanyProfilePreview,
) {
  return value(
    await api.post<{ active: true }>(
      `${companyRoot}/${encodeURIComponent(preview.company.id)}`,
      { expectedHash: preview.hash, consentConfirmed: true },
    ),
  );
}
export async function revokeCompanyProfileAccess(companyId: string) {
  return value(
    await api.delete<{ active: false }>(
      `${companyRoot}/${encodeURIComponent(companyId)}`,
    ),
  );
}
